import Moment from "moment";
import * as FileSaver from "file-saver";
import { SaleRepository, CompanyRepository } from "core/repository";
import { BasePresenter } from "core/presenter";
import { DocumentTypes } from "core/enum";

class SalesPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      content: [],
      company: {},
      loading: {
        results: true,
        revenues: true
      },

      filter: {
        startDate: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        endDate: endDate.isValid() ? endDate : Moment().endOf("day"),
        saleStatus: "all",
        documentNumber: "",
        documentSerie: ""
      },

      revenues: 0,

      order: {
        field: "activated_at",
        direction: "DESC"
      },

      pagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      }
    };
  }

  async onStart() {
    this.doLoadResults();
    this.calculateTotalRevenues();
  }

  async doLoadResults() {
    this.viewModel.loading.results = true;
    this.renewCancelToken();

    const repositoryResult = await SaleRepository.getListOnline({
      order: this.getOrder(),
      pagination: {
        page: this.viewModel.pagination.currentPage,
        perPage: this.viewModel.pagination.perPage
      },
      filters: await this.getFilters()
    });
    const currentUser = await this.mRuntime.get("currentUser");
    const company = await CompanyRepository.getOnline(currentUser.company_uuid);
    this.viewModel.companyFoo = company;
    if (repositoryResult.success) {
      const sales = this.parseResults(repositoryResult.data);
      const paymentDic = {};
      company.data.custom_other_payments.forEach(item => {
        const uuid_option = item.uuid;
        const label_option = item.label;
        paymentDic[uuid_option] = label_option;
      });
      sales.forEach(sale => {
        sale.payments.forEach(payment => {
          if (payment.type === "others") {
            payment.type =
              paymentDic[payment.metadata.other_payment_uuid] || payment.type;
          }
        });
      });
      this.viewModel.content = sales;
      this.viewModel.pagination = {
        ...this.viewModel.pagination,
        total: repositoryResult.total,
        perPage: repositoryResult.perPage
      };
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Ooopss...",
        content:
          "Não foi possível carregar as vendas.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }

    this.viewModel.loading.results = false;
  }

  getOrder() {
    const { order } = this.viewModel;
    const options = { direction: order.direction };

    if (order.field === "customer") {
      return [
        { direction: order.direction, field: "customer.name" },
        { direction: order.direction, field: "document.identification" },
        { direction: order.direction, field: "document.email" }
      ];
    } else if (order.field === "document_number") {
      return [
        { direction: order.direction, field: "document.type" },
        { direction: order.direction, field: "document.number" }
      ];
    } else {
      options.field = order.field;
    }

    return [options];
  }

  async calculateTotalRevenues() {
    this.viewModel.loading.revenues = true;

    this.viewModel.revenues = 0;
    const revenuesResult = await SaleRepository.totalRevenues([
      ...(await this.getFilters()),
      {
        connector: "AND",
        field: "status",
        operation: "EQ",
        value: "active"
      }
    ]);

    if (revenuesResult.success) {
      this.viewModel.revenues = revenuesResult.data;
    }

    this.viewModel.loading.revenues = false;
  }

  parseResults(results) {
    return results.map(sale => {
      sale.total_value = sale.total_value / 100;

      const { canCancel, cancelReason } = this.canCancel(sale);
      sale.can_cancel = canCancel;
      sale.cancel_reason = cancelReason;
      sale.document.type_name = DocumentTypes[sale.document.type];

      return sale;
    });
  }

  canCancel(sale) {
    if (sale.status === "cancelled") {
      return {
        canCancel: false,
        cancelReason: null
      };
    }

    switch (sale.document.type) {
      case "receipt":
        return {
          canCancel: true,
          cancelReason: ""
        };

      case "nfce":
        return {
          canCancel: true,
          cancelReason: ""
        };

      case "cfe":
        return {
          canCancel: false,
          cancelReason:
            "Realize o cancelamento pelo aplicativo android utilizando o mesmo SAT que emitiu esta venda."
        };

      default:
        return {
          canCancel: false,
          cancelReason: "Documento desconhecido"
        };
    }
  }

  async onRequestDelete(sale) {
    const additionalMessage = this._hasCardPayments(sale)
      ? "\nEsta venda possui pagamentos em cartão. Realizar o cancelamento não fará o estorno automático dos pagamentos."
      : "";

    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: `Tem certeza que deseja cancelar a venda ${sale.receiptName()}?${additionalMessage}`,
      actions: [
        {
          label: "Voltar"
        },
        {
          label: "Sim, eu tenho certeza",
          type: "accent",
          callback: () => this.doDelete(sale)
        }
      ]
    });
  }

  async doDelete(sale) {
    this.viewModel.loading.results = true;

    const repositoryResult = await SaleRepository.cancel(sale);

    if (repositoryResult.success) {
      this.sendNotification({
        type: "success",
        content: `Venda cancelada com sucesso.`
      });

      this.doLoadResults();
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          repositoryResult.error ||
          "Não conseguimos cancelar essa venda. Tente novamente mais tarde."
      });
    }

    this.viewModel.loading.results = false;
  }

  async onDownload(format) {
    this.viewModel.loading.results = true;

    switch (format) {
      case "csv":
        await this._downloadCSV();
        break;

      default:
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: "Ainda não suportamos esse formato"
        });
    }

    this.viewModel.loading.results = false;
  }

  _hasCardPayments(sale) {
    const cardTypes = ["credit_card", "debit_card"];
    const payment = sale.payments
      .map(payment => payment.type)
      .find(type => cardTypes.includes(type));
    return !!payment;
  }

  async _downloadCSV() {
    const repositoryResult = await SaleRepository.getCsvContent({
      order: this.getOrder(),
      filters: await this.getFilters()
    });
    if (!repositoryResult.success) {
      this.sendNotification({
        type: "error",
        title: "Ooopss...",
        content:
          "Não foi possível gerar o download do seu arquivo. Por favor tente novamente mais tarde.\nSe o problema persistir, entre em contato com o suporte."
      });

      return;
    }

    const content = repositoryResult.data;
    const contentBlob = new Blob([content], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "historico-de-vendas.csv");
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.doLoadResults();
  }

  onPageChange(page) {
    this.viewModel.pagination = {
      ...this.viewModel.pagination,
      currentPage: page
    };

    this.doLoadResults();
  }

  async getFilters() {
    const {
      startDate,
      endDate,
      saleStatus,
      documentNumber,
      documentSerie
    } = this.viewModel.filter;
    const currentUser = await this.mRuntime.get("currentUser");

    const filters = [
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: currentUser.company_uuid
      }
    ];

    if (startDate) {
      filters.push({
        connector: "AND",
        field: "activated_at",
        operation: "GTE",
        value: startDate.toISOString()
      });
    }

    if (endDate) {
      filters.push({
        connector: "AND",
        field: "activated_at",
        operation: "LTE",
        value: endDate.toISOString()
      });
    }

    if (saleStatus && saleStatus !== "all") {
      filters.push({
        connector: "AND",
        field: "status",
        operation: "EQ",
        value: saleStatus
      });
    } else {
      filters.push({
        connector: "AND",
        field: "status",
        operation: "NOT",
        value: "open"
      });
    }

    if (documentNumber && documentNumber !== "") {
      filters.push({
        connector: "AND",
        field: "document.number",
        operation: "EQ",
        value: documentNumber
      });
    }
    if (documentSerie && documentSerie !== "") {
      filters.push({
        connector: "AND",
        field: "document.serie",
        operation: "EQ",
        value: documentSerie
      });
    }

    return filters;
  }

  onFilterChange(filtered) {
    if (filtered.startDate && filtered.endDate) {
      window.location.hash = `?&inicio=${Moment(filtered.startDate).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filtered.endDate).format("DD-MM-YYYY")}`;
      filtered.startDate = filtered.startDate.startOf("day");
      filtered.endDate = filtered.endDate.endOf("day");
    }
    this.viewModel.filter = { ...this.viewModel.filter, ...filtered };
    this.viewModel.pagination.currentPage = 1;
    this.doLoadResults();
    this.calculateTotalRevenues();
  }

  hideSaleDialog = () => (this.viewModel.dialogs.sale.visible = false);
}

export default SalesPresenter;



// WEBPACK FOOTER //
// ./src/components/app/history/sales/presenter.js